import { ClickAwayListener, IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import React, { forwardRef, RefObject, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type TooltipProps = {
  title?: string;
  iconClassName?: string;
  tooltipContentClassName?: string;
  children?: any;
  place?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  dangerouslySetContent?: boolean;
  clickable?: boolean; // disables pointer-events: none
  tooltipProps?: TooltipProps;
  disableTitleTranslation?: boolean;
  trigger?: 'hover' | 'click';
  className?: string;
  disableHoverStyle?: boolean;
  disabled?: boolean;
  // fired when the tooltip opens — optional, backward-compatible (e.g. checkout info-toggle tracking)
  onOpen?: () => void;
};

export type TooltipRefType = {
  show: (visible: boolean) => void;
  toggle: () => void;
};

const TooltipYaga = forwardRef((props: TooltipProps, ref: RefObject<any>) => {
  const {
    children,
    title,
    iconClassName,
    tooltipContentClassName,
    place,
    trigger,
    dangerouslySetContent,
    disableTitleTranslation,
    tooltipProps,
    className,
    disableHoverStyle,
    disabled,
    clickable,
    onOpen,
  } = props;
  const [openTooltip, setOpenTooltip] = useState(false);
  const { t } = useTranslation();
  const position = place || 'top';

  useEffect(() => {
    if (openTooltip) {
      onOpen?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTooltip]);

  const triggerOption = trigger || 'click';
  const [ignoreMouseOverHandler, setIgnoreMouseOverHandler] = useState<boolean>(false);

  const handleOpen = () => {
    setOpenTooltip(!openTooltip);
  };

  const mouseOverHandler = (action) => {
    if (!ignoreMouseOverHandler) {
      setOpenTooltip(action);
    }

    setIgnoreMouseOverHandler(false);
  };

  // Expose data to ref
  useImperativeHandle(
    ref,
    (): TooltipRefType => ({
      show: (visible: boolean) => {
        setIgnoreMouseOverHandler(true);
        setOpenTooltip(visible);
      },
      toggle: () => {
        setIgnoreMouseOverHandler(true);
        handleOpen();
      },
    }),
  );

  if (disabled) {
    return children;
  }

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        // The tooltip body renders in a portal, so clicks inside it reach this handler as
        // click-aways. A clickable tooltip has to stay open long enough for a link in its
        // body to be followed.
        if (clickable && (event.target as Element)?.closest?.('.MuiTooltip-popper')) {
          return;
        }

        mouseOverHandler(false);
      }}
    >
      <Tooltip
        disableInteractive={!clickable}
        open={openTooltip}
        title={
          <span
            className={tooltipContentClassName}
            dangerouslySetInnerHTML={{
              __html: disableTitleTranslation ? title : t(title),
            }}
          />
        }
        enterDelay={100}
        placement={position}
        className={classNames('tooltip-btn', className, { 'hover-style-disabled': disableHoverStyle })}
        onClick={handleOpen}
        arrow
        PopperProps={{
          popperOptions: {
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  padding: 0,
                },
              },
            ],
          },
        }}
        {...tooltipProps}
      >
        <IconButton
          onMouseEnter={() => triggerOption === 'hover' && mouseOverHandler(true)}
          onMouseLeave={() => triggerOption === 'hover' && mouseOverHandler(false)}
          onClick={() => triggerOption === 'click' && mouseOverHandler(!openTooltip)}
          aria-label="info"
          className={classNames('tooltip-icon', iconClassName)}
        >
          {dangerouslySetContent ? <span dangerouslySetInnerHTML={{ __html: children }} /> : children}
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  );
});

export default TooltipYaga;
